import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";
import { 
  Receipt, Building2, Briefcase, CreditCard, Banknote, CheckCircle2, 
  Calculator, AlertTriangle, Lock, Unlock, Pencil, Stamp, Eye, User, FileText,
  Plus, Trash2, MoreHorizontal, Euro
} from "lucide-react";
import { cn } from "@/lib/utils";
import api from "@/api";
import { computeInvoiceTotals } from "@/lib/invoiceCalc";
import {
  buildBalanceDeductionRows,
  getPracticeBilling,
  resolvePracticeFeeMode,
} from "@/lib/practiceBilling";
import { confirmDirtyClose, useFormDirty } from "@/lib/dialogConfirm";

// ========== UTILITY FUNCTIONS FOR EU NUMBER FORMAT ==========
// Format number to Italian format: 1234.56 → "1.234,56"
const formatEU = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const num = parseFloat(value);
  if (isNaN(num)) return "";
  return num.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// Parse Italian format to number: "1.234,56" → 1234.56
const parseEU = (str) => {
  if (!str || str === "") return "";
  // Remove thousand separators (.) and replace decimal comma with dot
  const cleaned = str.replace(/\./g, "").replace(",", ".");
  const num = parseFloat(cleaned);
  return isNaN(num) ? "" : num;
};

// Handle keypad input: treat "." as decimal separator (convert to ",")
const normalizeDecimalInput = (value) => {
  // Replace dot with comma for decimal input
  return value.replace(".", ",");
};

export function InvoiceDialog({
  isOpen,
  onOpenChange,
  invoiceForm,
  setInvoiceForm,
  onSave,
  onReset,
  onPreview,
  clients,
  cases,
  invoices = [],
  isSaving,
  isPreviewLoading = false,
  isEditing = false,
  settings = {},
  saveError = null,
  clearSaveError = () => {},
  onCreateClient = null  // Optional: when provided, enables inline "+ Crea nuovo cliente"
}) {
  const [nextInvoiceNumber, setNextInvoiceNumber] = useState("");
  const [isLoadingNumber, setIsLoadingNumber] = useState(false);
  
  // State for invoice number editing lock
  const [isNumberLocked, setIsNumberLocked] = useState(true);
  
  // State for formatted display values (EU format)
  const [displayImponibile, setDisplayImponibile] = useState("");
  const [displaySpeseArt15, setDisplaySpeseArt15] = useState("");
  
  // State for Lordo/Netto toggle - check explicitly for true
  const importoLordo = invoiceForm.importoLordo === true;
  
  // State for multiple linked practices
  const [linkedPractices, setLinkedPractices] = useState([]);
  
  // State for manual/free rows
  const [manualRows, setManualRows] = useState([]);
  
  // Add a linked practice
  const addLinkedPractice = (practiceId) => {
    if (practiceId && !linkedPractices.includes(practiceId)) {
      setLinkedPractices([...linkedPractices, practiceId]);
    }
  };
  
  // Remove a linked practice
  const removeLinkedPractice = (practiceId) => {
    setLinkedPractices(linkedPractices.filter(id => id !== practiceId));
  };
  
  // Add a manual row
  const addManualRow = () => {
    setManualRows([...manualRows, { id: Date.now(), description: "", amount: "", displayAmount: "" }]);
  };
  
  // Format amount in Italian style (1500 -> 1.500,00)
  const formatManualRowAmount = (value) => {
    if (!value) return "";
    const numValue = parseFloat(parseEU(value)) || 0;
    if (numValue === 0 && value !== "0") return value; // Keep typing in progress
    return formatEU(numValue);
  };
  
  // Update a manual row
  const updateManualRow = (id, field, value) => {
    setManualRows(manualRows.map(row => {
      if (row.id !== id) return row;
      if (field === 'amount') {
        // Normalize input and store raw value
        const normalized = normalizeDecimalInput(value);
        return { ...row, amount: normalized, displayAmount: normalized };
      }
      return { ...row, [field]: value };
    }));
  };
  
  // Handle blur on amount field - format it
  const handleManualRowAmountBlur = (id) => {
    setManualRows(manualRows.map(row => {
      if (row.id !== id) return row;
      const formatted = formatManualRowAmount(row.amount);
      const numValue = parseFloat(parseEU(row.amount)) || 0;
      return { ...row, amount: String(numValue), displayAmount: formatted };
    }));
  };
  
  // Remove a manual row
  const removeManualRow = (id) => {
    setManualRows(manualRows.filter(row => row.id !== id));
  };
  
  // NOTE: manualRowsTotal is now derived from `totals.manualRowsTotalNet`
  // (see the centralized engine below). The legacy useMemo here previously
  // summed raw inputs which was correct in NET mode but wrong in LORDO mode.

  // Reset linked practices and manual rows when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setIsNumberLocked(true);
      // Reset preview number to avoid showing stale value from previous dialog
      if (!isEditing) {
        setNextInvoiceNumber("");
      }
      if (invoiceForm.imponibile) {
        setDisplayImponibile(formatEU(invoiceForm.imponibile));
      } else {
        setDisplayImponibile("");
      }
      if (invoiceForm.speseArt15) {
        setDisplaySpeseArt15(formatEU(invoiceForm.speseArt15));
      } else {
        setDisplaySpeseArt15("");
      }
      // Initialize linked practices from form if editing
      if (invoiceForm.linkedPractices) {
        setLinkedPractices(invoiceForm.linkedPractices);
      } else if (invoiceForm.caseId) {
        setLinkedPractices([invoiceForm.caseId]);
      } else {
        setLinkedPractices([]);
      }
      // Initialize manual rows from form if editing
      // Initialize manual rows from form if editing.
      // Roundtrip rule: each row's `amount` in dialog state holds what the
      // operator originally typed (gross in LORDO mode, net in NET mode).
      // Newer payloads carry `amountInput`; older ones only carry `amount`
      // (which was the typed value back when LORDO mode for manual rows
      // wasn't yet supported, i.e. effectively net). In both cases we
      // surface that value to the operator.
      if (invoiceForm.manualRows) {
        const restored = invoiceForm.manualRows.map((row) => {
          const operatorAmount =
            row.amountInput !== undefined && row.amountInput !== null
              ? row.amountInput
              : row.amount;
          const numeric = typeof operatorAmount === "number"
            ? operatorAmount
            : parseFloat(parseEU(operatorAmount)) || 0;
          return {
            id: row.id || `restored-${Math.random().toString(36).slice(2, 9)}`,
            description: row.description || "",
            amount: String(numeric),
            displayAmount: formatEU(numeric),
            isAdvanceDeduction: row.isAdvanceDeduction === true,
            sourceInvoiceId: row.sourceInvoiceId || null,
          };
        });
        setManualRows(restored);
      } else {
        setManualRows([]);
      }

      // Auto-set the amount mode based on the preselected client (covers the
      // immediate-invoice flow where the client is already in the form when
      // the dialog opens). Applied only on NEW invoices.
      //
      // Rule (matches handleClientChange):
      //   - Privato / no VAT  → LORDO
      //   - Società / Professionista / Ditta individuale → NETTO
      if (!isEditing && invoiceForm.clientId) {
        const sel = clients.find(c => String(c.id) === String(invoiceForm.clientId));
        if (sel) {
          const isPrivateNoVat = sel.clientType === "Privato"
            || !sel.vatNumber
            || String(sel.vatNumber).trim() === "";
          // Apply the rule only when the operator hasn't yet flipped the
          // toggle in this dialog session. We treat `undefined` (initial
          // load) as "not flipped". If the user has already manually
          // toggled, we keep their choice.
          if (invoiceForm.importoLordo === undefined) {
            setInvoiceForm(prev => ({ ...prev, importoLordo: isPrivateNoVat }));
          }
        }
      }
    }
  }, [isOpen]);
  
  // State for auto-calculation toggle
  const contributoEnabled = invoiceForm.contributoEnabled !== false; // Default true

  // ─── Centralized totals engine ───────────────────────────────────────────
  // All amounts that drive the UI breakdown and the save payload come from
  // computeInvoiceTotals(). The engine reverse-calculates from gross to net
  // when importoLordo is true, applying the same divisor to the main amount
  // AND to every manual row, so a €200 gross total stays €200.
  const ivaPercentage = parseFloat(invoiceForm.iva) || 0;
  const speseArt15Input = parseFloat(invoiceForm.speseArt15) || 0;
  // 11/06/2026 — Spese di Spedizione (non soggette a IVA, additivo).
  const shippingExpensesInput = parseFloat(invoiceForm.shippingExpensesAmount) || 0;
  const totals = useMemo(
    () =>
      computeInvoiceTotals({
        imponibileInput: invoiceForm.imponibile,
        manualRows,
        importoLordo,
        ivaPercentage,
        contributoEnabled,
        contributoIntegrativoManual: invoiceForm.contributoIntegrativo,
        speseArt15Input,
        shippingExpensesInput,
      }),
    [
      invoiceForm.imponibile,
      manualRows,
      importoLordo,
      ivaPercentage,
      contributoEnabled,
      invoiceForm.contributoIntegrativo,
      speseArt15Input,
      shippingExpensesInput,
    ]
  );

  // Stable references — kept under the old names to minimise downstream churn
  const imponibile = parseFloat(invoiceForm.imponibile) || 0;
  const imponibileEffettivo = totals.imponibilePrincipaleNet;
  const manualRowsTotalInput = totals.manualRowsTotalInput; // gross in LORDO
  const manualRowsTotal = totals.manualRowsTotalNet;        // net taxable
  const imponibileWithManualRows = totals.totalTaxableNet;
  const contributoIntegrativo = totals.contributoIntegrativo;
  const baseIVA = totals.baseIVA;
  const ivaAmount = totals.ivaAmount;
  const bolloAutomatico = totals.bolloAutomatico;
  const speseArt15 = totals.speseArt15Total;
  const subtotale = totals.subtotale;
  const arrotondamento = totals.arrotondamento;
  const totale = totals.totale;

  // ─── Art.15 over-flow guard ──────────────────────────────────────────────
  // In LORDO mode the operator-entered gross IS the final invoice total
  // and Art.15 bolli are absorbed from it (see invoiceCalc Scenario G/H/I).
  // When the entered Art.15 (raw + auto bollo) exceeds the gross pool
  // there's nothing left to absorb from → the system clamps the
  // reduction and the resulting invoice is inconsistent. Surface the
  // problem to the operator BEFORE they save.
  const grossPoolForArt15 = (() => {
    const main = parseFloat(invoiceForm.imponibile) || 0;
    const rowsSum = manualRows.reduce(
      (s, r) =>
        s + (parseFloat(String(r?.amount ?? r?.amountInput ?? 0).replace(",", ".")) || 0),
      0
    );
    return Math.round((main + rowsSum) * 100) / 100;
  })();
  const art15ExceedsLordo =
    importoLordo && speseArt15 > 0 && grossPoolForArt15 > 0 && speseArt15 > grossPoolForArt15;

  // Update contributo value when imponibile changes (if auto-calc enabled)
  useEffect(() => {
    if (contributoEnabled && imponibileWithManualRows > 0) {
      const autoContrib = Math.round(imponibileWithManualRows * 0.04 * 100) / 100;
      if (parseFloat(invoiceForm.contributoIntegrativo) !== autoContrib) {
        setInvoiceForm(prev => ({ 
          ...prev, 
          contributoIntegrativo: autoContrib.toFixed(2) 
        }));
      }
    }
  }, [imponibileWithManualRows, contributoEnabled]);

  // Toggle contributo enabled/disabled
  const handleContributoToggle = useCallback((enabled) => {
    const currentImponibile = importoLordo ? imponibileEffettivo : (parseFloat(invoiceForm.imponibile) || 0);
    setInvoiceForm(prev => ({
      ...prev,
      contributoEnabled: enabled,
      contributoIntegrativo: enabled 
        ? Math.round(currentImponibile * 0.04 * 100) / 100
        : 0
    }));
  }, [setInvoiceForm, importoLordo, imponibileEffettivo, invoiceForm.imponibile]);

  // Toggle Lordo/Netto mode
  const handleImportoLordoToggle = useCallback((isLordo) => {
    setInvoiceForm(prev => ({
      ...prev,
      importoLordo: isLordo
    }));
  }, [setInvoiceForm]);

  // Handle imponibile input change with EU format
  const handleImponibileChange = (e) => {
    const rawValue = normalizeDecimalInput(e.target.value);
    setDisplayImponibile(rawValue);
  };
  
  // Handle imponibile blur - format and save
  const handleImponibileBlur = () => {
    const numValue = parseEU(displayImponibile);
    if (numValue !== "") {
      setDisplayImponibile(formatEU(numValue));
      setInvoiceForm(prev => ({ ...prev, imponibile: String(numValue) }));
    } else {
      setDisplayImponibile("");
      setInvoiceForm(prev => ({ ...prev, imponibile: "" }));
    }
  };
  
  // Handle speseArt15 input change with EU format
  const handleSpeseArt15Change = (e) => {
    const rawValue = normalizeDecimalInput(e.target.value);
    setDisplaySpeseArt15(rawValue);
  };
  
  // Handle speseArt15 blur - format and save
  const handleSpeseArt15Blur = () => {
    const numValue = parseEU(displaySpeseArt15);
    if (numValue !== "") {
      setDisplaySpeseArt15(formatEU(numValue));
      setInvoiceForm(prev => ({ ...prev, speseArt15: String(numValue) }));
    } else {
      setDisplaySpeseArt15("");
      setInvoiceForm(prev => ({ ...prev, speseArt15: "" }));
    }
  };

  // Check if payment method allows immediate payment marking
  const canMarkAsPaid = invoiceForm.paymentMethod === "Contanti" || 
                        invoiceForm.paymentMethod === "POS" || 
                        invoiceForm.paymentMethod === "Carta" ||
                        invoiceForm.paymentMethod === "ContantiVistaFattura";
  
  // Check if IBAN info is available for Bonifico method
  const showIbanInfo = invoiceForm.paymentMethod === "Bonifico" && 
                       (settings?.iban || settings?.bankName);
  
  // Prepare items for searchable client combobox
  const clientItems = useMemo(() => {
    return clients.map(client => ({
      value: String(client.id),
      label: client.fullName,
      searchTerms: `${client.fullName} ${client.taxCode || ""} ${client.vatNumber || ""} ${client.email || ""}`,
      client: client
    }));
  }, [clients]);
  
  // Prepare items for searchable case combobox (filtered by selected client)
  // Exclude practices already linked to an existing invoice
  const caseItems = useMemo(() => {
    const filteredCases = invoiceForm.clientId 
      ? cases.filter(c => String(c.clientId) === invoiceForm.clientId)
      : cases;
    
    const enrichedCases = filteredCases.map((caseItem) => ({
      ...caseItem,
      _billing: getPracticeBilling(caseItem, invoices, clients),
    }));
    const availableCases = enrichedCases.filter(c => !c._billing.fullyInvoiced);
    const invoicedCases = enrichedCases.filter(c => c._billing.fullyInvoiced);
    
    return [
      { value: "none", label: "— Nessuna pratica —", searchTerms: "" },
      ...availableCases.map(caseItem => ({
        value: String(caseItem.id),
        label: `${caseItem.caseNumber} - ${caseItem.subject}${
          caseItem._billing.partiallyInvoiced
            ? ` [RESIDUO ${formatEU(caseItem._billing.residualAmount)} €]`
            : ""
        }`,
        searchTerms: `${caseItem.caseNumber} ${caseItem.subject} ${caseItem.practiceArea || ""} ${caseItem.enteCoinvolto || ""}`,
        caseData: caseItem,
        alreadyInvoiced: false
      })),
      ...invoicedCases.map(caseItem => ({
        value: String(caseItem.id),
        label: `${caseItem.caseNumber} - ${caseItem.subject} [GIA FATTURATA]`,
        searchTerms: `${caseItem.caseNumber} ${caseItem.subject}`,
        caseData: caseItem,
        alreadyInvoiced: true,
        disabled: true
      }))
    ];
  }, [cases, invoices, clients, invoiceForm.clientId]);

  // 11/06/2026 — Safety net: se l'utente cambia cliente in editor (o
  // entra in editing su una fattura legacy) e il nuovo cliente NON ha
  // `splitPaymentEnabled === true`, forziamo il flag a false sulla
  // fattura. Impedisce attivazioni accidentali residue: il regime
  // Split Payment può essere applicato SOLO a clienti per cui è
  // espressamente abilitato in anagrafica.
  React.useEffect(() => {
    if (!invoiceForm.clientId) return;
    const sel = clients.find((c) => String(c.id) === String(invoiceForm.clientId));
    const clientAllowsSplit = !!(sel && sel.splitPaymentEnabled === true);
    if (!clientAllowsSplit && invoiceForm.splitPaymentEnabled) {
      setInvoiceForm((prev) => ({ ...prev, splitPaymentEnabled: false }));
    }
  }, [invoiceForm.clientId, clients]); // eslint-disable-line react-hooks/exhaustive-deps

  // State for warnings
  const [dateWarning, setDateWarning] = useState(null);
  const [managementChangeWarning, setManagementChangeWarning] = useState(null);

  // Validate date for chronological consistency
  useEffect(() => {
    const checkDate = async () => {
      if (!invoiceForm.invoiceDate || !invoiceForm.managementType) {
        setDateWarning(null);
        return;
      }
      try {
        const result = await api.request(`/api/invoices/validate-date/${invoiceForm.managementType}?date=${invoiceForm.invoiceDate}`);
        setDateWarning(result.warning || null);
      } catch {
        setDateWarning(null);
      }
    };
    if (isOpen) checkDate();
  }, [invoiceForm.invoiceDate, invoiceForm.managementType, isOpen]);

  // Fetch next invoice number when management type or date year changes (for new invoices)
  useEffect(() => {
    const fetchNextNumber = async () => {
      if (!invoiceForm.managementType || isEditing) return;
      
      // Extract year from invoice date
      let year;
      try {
        year = invoiceForm.invoiceDate ? parseInt(invoiceForm.invoiceDate.split("-")[0]) : new Date().getFullYear();
      } catch { year = new Date().getFullYear(); }
      
      setIsLoadingNumber(true);
      try {
        const result = await api.getNextInvoiceNumber(
          invoiceForm.managementType,
          year,
          invoiceForm.documentType || "invoice"
        );
        setNextInvoiceNumber(result.nextNumber);
        // Always update the number when management type/document type changes for new invoices
        setInvoiceForm(prev => ({ ...prev, invoiceNumber: result.nextNumber }));
      } catch (error) {
        console.error("Error fetching next invoice number:", error);
      } finally {
        setIsLoadingNumber(false);
      }
    };

    if (isOpen && !isEditing) {
      fetchNextNumber();
    }
  }, [invoiceForm.managementType, invoiceForm.invoiceDate, invoiceForm.documentType, isOpen, isEditing]);

  // Auto-fill management type when client is selected
  const handleClientChange = (clientId) => {
    const selectedClient = clients.find(c => String(c.id) === clientId);
    const clientManagementType = selectedClient?.managementType || "studio";

    // Default amount mode per client type:
    //   - Privato / no VAT number     → LORDO IVA inclusa
    //   - Società / Professionista /  → NETTO (operator types taxable amount,
    //     Ditta individuale             system adds contributo + IVA on top)
    // The operator can still toggle manually after the client is picked.
    const isPrivateNoVat = selectedClient
      ? (selectedClient.clientType === "Privato" ||
         !selectedClient.vatNumber || String(selectedClient.vatNumber).trim() === "")
      : false;

    setInvoiceForm({
      ...invoiceForm,
      clientId,
      managementType: clientManagementType,
      // Force the rule strictly: private → lordo, company/pro/sole-prop → netto.
      // We intentionally DO NOT preserve a previous LORDO=true when the new
      // client is a company/professional, because that combination produced
      // the bug where €200 became €254 on company invoices.
      importoLordo: isPrivateNoVat,
      // Split Payment (07/06/2026) — eredita il flag dal cliente. Se
      // l'utente cambia cliente, il regime viene ricalcolato in base alla
      // nuova anagrafica (puo' essere overridato manualmente dopo).
      splitPaymentEnabled: !!(selectedClient && selectedClient.splitPaymentEnabled),
      // Clear invoice number to trigger re-fetch
      invoiceNumber: "",
      // Clear case when client changes
      caseId: ""
    });
  };

  // Handle case selection and auto-fill description + spese art. 15 (diritti e bolli from practice)
  const handleCaseChange = (value) => {
    const selectedCaseId = value === "none" ? "" : value;
    let autoFilledDescription = invoiceForm.notes;
    let autoFilledSpeseArt15 = invoiceForm.speseArt15;

    if (selectedCaseId) {
      const selectedCase = cases.find(c => String(c.id) === selectedCaseId);
      if (selectedCase) {
        const parts = [];
        if (selectedCase.caseNumber) parts.push(`Pratica: ${selectedCase.caseNumber}`);
        if (selectedCase.subject) parts.push(selectedCase.subject);
        if (selectedCase.practiceArea) parts.push(`Tipo: ${selectedCase.practiceArea}`);
        if (selectedCase.enteCoinvolto) parts.push(`Ente: ${selectedCase.enteCoinvolto}`);
        if (selectedCase.internalNotes) parts.push(`Note: ${selectedCase.internalNotes}`);
        autoFilledDescription = parts.join(" - ");

        // Auto-prefill Spese Art. 15 with practice's "Di cui diritti e bolli"
        // (fallback to legacy importoDiritti + importoBolli for old records).
        // Only prefill when the field is empty, so we never overwrite operator input.
        const currentSpese = parseFloat(invoiceForm.speseArt15) || 0;
        if (!currentSpese) {
          const dirittiBolli = Number(selectedCase.importoDirittiBolli)
            || ((Number(selectedCase.importoDiritti) || 0) + (Number(selectedCase.importoBolli) || 0));
          if (dirittiBolli > 0) {
            autoFilledSpeseArt15 = String(dirittiBolli);
            setDisplaySpeseArt15(formatEU(dirittiBolli));
          }
        }
      }
    }

    setInvoiceForm({
      ...invoiceForm,
      caseId: selectedCaseId,
      notes: autoFilledDescription,
      speseArt15: autoFilledSpeseArt15,
    });
  };

  // Handle management type change with confirmation for editing
  const handleManagementTypeChange = (value) => {
    if (isEditing && invoiceForm.managementType && invoiceForm.managementType !== value) {
      const confirmed = window.confirm(
        `Attenzione: cambiando la gestione da "${invoiceForm.managementType === 'studio' ? 'Studio' : 'Agenzia'}" a "${value === 'studio' ? 'Studio' : 'Agenzia'}", il numero fattura verra' riassegnato dalla nuova sequenza.\n\nContinuare?`
      );
      if (!confirmed) return;
    }
    setInvoiceForm({
      ...invoiceForm,
      managementType: value,
      invoiceNumber: ""
    });
  };
  
  /**
   * Build the manual-rows array for the payload (preview + save).
   *
   * IMPORTANT (LORDO mode):
   *  - `amount` is the SCORPORATED NET amount: this is what the backend PDF
   *    sums together with `imponibile` to produce a coherent imponibile
   *    total that matches `ivaAmount` and `totale`.
   *  - `amountInput` preserves the operator-typed gross amount so that the
   *    dialog can re-hydrate the same input on edit (round-trip safe).
   *  - `amountMode` records which mode the row was created in.
   *
   * Rows whose description is empty AND whose amount is zero are dropped.
   */
  const buildCleanManualRows = () =>
    totals.manualRowsBreakdown
      .filter((r) => r.description || r.amountInput > 0)
      .map((r) => ({
        description: r.description,
        amount: r.amountNet,
        amountInput: r.amountInput,
        amountMode: importoLordo ? "lordo" : "netto",
        isAdvanceDeduction: r.isAdvanceDeduction === true,
        sourceInvoiceId: r.sourceInvoiceId || null,
      }));

  // Handle preview with calculated values
  const handlePreview = () => {
    const cleanManualRows = buildCleanManualRows();

    // Pass calculated values to preview
    const previewData = {
      ...invoiceForm,
      imponibile: imponibileEffettivo,
      contributoIntegrativo,
      speseArt15: speseArt15Input, // ORIGINAL value without bollo
      bolloAutomatico,
      iva: ivaPercentage,
      ivaAmount,
      arrotondamento,
      totale,
      manualRows: cleanManualRows,
      importoLordo: !!importoLordo,
      markAsPaid: invoiceForm.markAsPaid || false,
      // 11/06/2026 — Bug fix Spese di Spedizione su Anteprima PDF.
      // Se l'operatore clicca "Anteprima PDF" senza prima fare blur sul
      // campo, `invoiceForm.shippingExpensesAmount` può ancora essere
      // una stringa (es. "20", "20,00"). Il backend Pydantic accetta
      // "20" ma RIFIUTA "20,00" (separatore italiano). Normalizziamo
      // qui esattamente come fa `handleSave`, così la preview riflette
      // sempre il valore visto in UI.
      shippingExpensesAmount: (() => {
        const raw = String(invoiceForm.shippingExpensesAmount ?? "").replace(",", ".");
        const n = parseFloat(raw);
        return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
      })(),
      splitPaymentEnabled: !!invoiceForm.splitPaymentEnabled,
    };
    onPreview(previewData);
  };

  // Handle save with calculated values
  const handleSave = () => {
    const cleanManualRows = buildCleanManualRows();

    // Resolve the actual invoice number to save
    const resolvedInvoiceNumber =
      invoiceForm.invoiceNumber || nextInvoiceNumber || "";

    // Build the complete save payload from the centralized engine output.
    // We pass it directly to `onSave(saveData)` instead of round-tripping
    // through `setInvoiceForm` + `flushSync`: the previous flushSync trick
    // was fragile because the parent's `onSave` callback closure was
    // captured BEFORE the synchronous re-render, so manualRows pushed
    // into invoiceForm were invisible to the save handler.
    const saveData = {
      ...invoiceForm,
      invoiceNumber: resolvedInvoiceNumber,
      imponibile: String(imponibileEffettivo),
      contributoIntegrativo,
      speseArt15: String(speseArt15Input),
      // 11/06/2026 — Bug fix: normalizza `shippingExpensesAmount` a
      // numero al save. Se l'utente clicca "Crea Fattura" senza fare
      // blur sull'input, il valore sarebbe ancora la stringa raw
      // ("5,00") e il backend la rifiuterebbe o il PDF la leggerebbe
      // come 0. Convertiamo qui per sicurezza.
      shippingExpensesAmount: (() => {
        const raw = String(invoiceForm.shippingExpensesAmount ?? "").replace(",", ".");
        const n = parseFloat(raw);
        return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : 0;
      })(),
      bolloAutomatico,
      iva: ivaPercentage,
      ivaAmount,
      arrotondamento,
      totale,
      manualRows: cleanManualRows,
      importoLordo: !!importoLordo,
      linkedPractices: linkedPractices,
    };
    // Mirror the values back into form state for any consumer that reads
    // `invoiceForm` after this point (e.g. the preview modal that uses
    // the form to label the in-app preview).
    setInvoiceForm(saveData);
    onSave(saveData);
  };

  // Dirty-tracking guard: confirm before discarding unsaved invoice data.
  // We track BOTH the form and the manualRows list since they're managed
  // in separate state slots.
  const dirtySnapshot = useMemo(
    () => ({ form: invoiceForm, manualRows }),
    [invoiceForm, manualRows]
  );
  const isDirty = useFormDirty(dirtySnapshot, isOpen);
  const guardedOpenChange = (next) => {
    if (!next && !confirmDirtyClose(isDirty)) return;
    onOpenChange(next);
  };
  const guardedReset = () => {
    if (!confirmDirtyClose(isDirty)) return;
    onReset();
  };

  return (
    <Dialog open={isOpen} onOpenChange={guardedOpenChange}>
      <DialogTrigger asChild>
        <Button
          data-testid="new-invoice-button"
          variant="outline"
          size="sm"
          className="gis-border-green gis-text-green hover:bg-[var(--gis-green-50)] hover:scale-105 shadow-sm transition-all duration-200"
          onClick={() => {
            onReset();
            onOpenChange(true);
          }}
        >
          <Receipt className="mr-2 h-4 w-4" />
          Nuova Fattura
        </Button>
      </DialogTrigger>
      <DialogContent className="!max-w-[1120px] w-[calc(100vw-2rem)] p-0 gap-0 overflow-hidden border-t-[6px] border-t-[var(--gis-green)]" data-testid="invoice-dialog-content">
        <DialogHeader>
          {/* Titolo dialog dinamico (28/02/2026): cambia in base al
              documentType per riusare lo stesso form sia per fatture
              fiscali sia per Documenti Proforma. */}
          {(() => {
            const isProforma = (invoiceForm.documentType === "proforma");
            const docLabel = isProforma ? "Documento Proforma" : "Fattura";
            return (
              <DialogTitle data-testid="invoice-dialog-title" className="gis-heading" style={{ color: "var(--gis-green)" }}>
                {isEditing ? `Modifica ${docLabel}` : `Nuovo ${docLabel}`}
              </DialogTitle>
            );
          })()}
        </DialogHeader>
        <div data-testid="invoice-dialog-body">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-4 gap-y-3 py-2">
          {/* Management Type */}
          <div className="lg:col-span-4">
            <Label htmlFor="managementType">Tipo Gestione *</Label>
            <Select
              value={invoiceForm.managementType || "studio"}
              onValueChange={handleManagementTypeChange}
            >
              <SelectTrigger data-testid="invoice-management-type-select">
                <SelectValue placeholder="Seleziona tipo gestione" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="studio">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-teal-600" />
                    Gestione Studio
                  </div>
                </SelectItem>
                <SelectItem value="agenzia">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-orange-600" />
                    Gestione Agenzia
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-1">
              Determina la numerazione e i dati di intestazione della fattura
            </p>
          </div>
          
          {/* Invoice Number - Protected Field */}
          <div className="lg:col-span-4">
            <div className="flex items-center justify-between mb-1">
              <Label htmlFor="invoiceNumber" className="flex items-center gap-1">
                {isNumberLocked ? (
                  <Lock className="h-3 w-3 text-slate-400" />
                ) : (
                  <Unlock className="h-3 w-3 text-amber-500" />
                )}
                Numero Fattura *
              </Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className={cn(
                  "h-6 px-2 text-xs gap-1",
                  isNumberLocked 
                    ? "text-slate-500 hover:text-amber-600" 
                    : "text-amber-600 hover:text-slate-600"
                )}
                onClick={() => setIsNumberLocked(!isNumberLocked)}
                data-testid="toggle-number-lock"
              >
                {isNumberLocked ? (
                  <>
                    <Pencil className="h-3 w-3" />
                    Modifica
                  </>
                ) : (
                  <>
                    <Lock className="h-3 w-3" />
                    Blocca
                  </>
                )}
              </Button>
            </div>
            <div className="relative">
              <Input
                id="invoiceNumber"
                data-testid="invoice-number-input"
                value={invoiceForm.invoiceNumber || nextInvoiceNumber}
                onChange={(e) => {
                  if (!isNumberLocked) {
                    setInvoiceForm({ ...invoiceForm, invoiceNumber: e.target.value });
                  }
                }}
                onFocus={(e) => {
                  // Prevent accidental editing when locked
                  if (isNumberLocked) {
                    e.target.blur();
                  }
                }}
                placeholder={isLoadingNumber ? "Caricamento..." : "Es. S-1/2026"}
                disabled={isLoadingNumber}
                readOnly={isNumberLocked}
                className={cn(
                  isNumberLocked 
                    ? "bg-slate-50 cursor-not-allowed border-slate-200 text-slate-700" 
                    : "bg-amber-50 border-amber-300 focus:border-amber-500"
                )}
              />
              {isNumberLocked && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                  <Lock className="h-4 w-4 text-slate-300" />
                </div>
              )}
            </div>
            <p className={cn(
              "text-xs mt-1",
              isNumberLocked ? "text-slate-500" : "text-amber-600 font-medium"
            )}>
              {isNumberLocked ? (
                <>
                  {invoiceForm.managementType === "agenzia" ? "Serie P (Agenzia)" : "Serie A (Studio)"} — Numerazione automatica
                </>
              ) : (
                <>Modifica manuale attiva — Assicurati che il numero sia univoco</>
              )}
            </p>
          </div>
          
          <div className="lg:col-span-4">
            <Label htmlFor="invoiceDate">Data Fattura *</Label>
            <Input
              id="invoiceDate"
              type="date"
              data-testid="invoice-date-input"
              value={invoiceForm.invoiceDate}
              onChange={(e) =>
                setInvoiceForm({ ...invoiceForm, invoiceDate: e.target.value })
              }
            />
            {dateWarning && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <span>&#9888;</span> {dateWarning}
              </p>
            )}
          </div>
          <div className="lg:col-span-6">
            <Label htmlFor="clientId">Cliente *</Label>
            <SearchableCombobox
              items={clientItems}
              value={invoiceForm.clientId}
              onValueChange={handleClientChange}
              placeholder="Cerca cliente..."
              searchPlaceholder="Digita nome, CF, P.IVA..."
              emptyMessage="Nessun cliente trovato."
              onCreate={onCreateClient || undefined}
              createButtonLabel="Crea nuovo cliente {q}"
              renderItem={(item) => (
                <div className="flex items-center gap-2 min-w-0">
                  <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium truncate">{item.client.fullName}</div>
                    {item.client.taxCode && (
                      <div className="text-xs text-slate-500 truncate">CF: {item.client.taxCode}</div>
                    )}
                  </div>
                </div>
              )}
              renderSelectedItem={(item) => (
                <span className="truncate">{item.client.fullName}</span>
              )}
              data-testid="invoice-client-select"
            />
          </div>
          
          {/* Searchable Case Combobox - Multiple practices support */}
          <div className="lg:col-span-6">
            <Label htmlFor="caseId" className="flex items-center gap-2">
              Pratiche Collegate
              <span className="text-xs text-slate-500">({linkedPractices.length} selezionate)</span>
            </Label>
            
            {/* Selected practices list */}
            {linkedPractices.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2 p-2 bg-slate-50 rounded-md">
                {linkedPractices.map(practiceId => {
                  const practice = cases.find(c => c.id === practiceId);
                  return practice ? (
                    <div key={practiceId} className="flex items-center gap-1 bg-white border rounded-md px-2 py-1 text-sm">
                      <FileText className="h-3 w-3 text-slate-400" />
                      <span className="font-mono text-xs">{practice.caseNumber}</span>
                      <button 
                        type="button"
                        onClick={() => removeLinkedPractice(practiceId)}
                        className="text-red-400 hover:text-red-600 ml-1"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ) : null;
                })}
              </div>
            )}

            {invoiceForm.billingStage && invoiceForm.practiceAgreedAmount > 0 && (
              <div className="mb-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                <strong>{invoiceForm.billingStage === "saldo" ? "Fattura a saldo" : "Fattura di acconto"}</strong>
                {" · "}Pattuito {formatEU(invoiceForm.practiceAgreedAmount)} €
                {" · "}Residuo prima di questa fattura {formatEU(invoiceForm.practiceResidualAmount)} €
                {" · "}{invoiceForm.practiceFeeMode === "lordo" ? "Lordo IVA inclusa" : "Netto imponibile"}
              </div>
            )}
            
            {/* Add practice dropdown */}
            <SearchableCombobox
              items={caseItems.filter(item => {
                // Always show "none" option
                if (item.value === "none") return true;
                // Hide already linked practices
                if (linkedPractices.includes(item.value)) return false;
                // Hide already invoiced practices (unless editing that invoice)
                if (item.alreadyInvoiced) return false;
                return true;
              })}
              value=""
              onValueChange={(selectedCaseId) => {
                if (selectedCaseId && selectedCaseId !== "none") {
                  // Double-check: block already invoiced
                  const item = caseItems.find(i => i.value === selectedCaseId);
                  if (item?.alreadyInvoiced) return;
                  
                  addLinkedPractice(selectedCaseId);
                  
                  const selectedCase = cases.find(c => String(c.id) === selectedCaseId);
                  const updates = {};
                  const billing = getPracticeBilling(selectedCase, invoices, clients);
                  const feeMode = resolvePracticeFeeMode(selectedCase, invoices, clients);
                  const isBalance = ["Conclusa", "Chiusa", "Completata"].includes(selectedCase?.status);
                  
                  // Set as primary caseId for compatibility
                  if (!invoiceForm.caseId) {
                    updates.caseId = selectedCaseId;
                  }
                  
                  if (isBalance && billing.agreedAmount > 0) {
                    updates.imponibile = String(billing.agreedAmount);
                    updates.billingStage = "saldo";
                    updates.importoLordo = feeMode === "lordo";
                    updates.practiceAgreedAmount = billing.agreedAmount;
                    updates.practiceResidualAmount = billing.residualAmount;
                    updates.practiceFeeMode = feeMode;
                    setDisplayImponibile(formatEU(billing.agreedAmount));
                    setManualRows(buildBalanceDeductionRows(billing));
                  } else {
                    updates.imponibile = "";
                    updates.billingStage = "acconto";
                    updates.importoLordo = feeMode === "lordo";
                    updates.practiceAgreedAmount = billing.agreedAmount;
                    updates.practiceResidualAmount = billing.residualAmount;
                    updates.practiceFeeMode = feeMode;
                    setDisplayImponibile("");
                    setManualRows([]);
                  }
                  
                  // Auto-prefill description from practice
                  if (selectedCase && (!invoiceForm.notes || invoiceForm.notes === "")) {
                    const parts = [];
                    if (selectedCase.caseNumber) parts.push(`Pratica: ${selectedCase.caseNumber}`);
                    if (selectedCase.subject) parts.push(selectedCase.subject);
                    if (selectedCase.practiceArea) parts.push(`Tipo: ${selectedCase.practiceArea}`);
                    if (selectedCase.enteCoinvolto) parts.push(`Ente: ${selectedCase.enteCoinvolto}`);
                    if (parts.length > 0) updates.notes = parts.join(" - ");
                  }
                  
                  // Auto-prefill Spese Art. 15 with practice's "Di cui diritti e bolli"
                  // (fallback to legacy importoDiritti + importoBolli for old records).
                  // Only prefill when the field is empty to avoid overwriting operator input.
                  const currentSpese = parseFloat(invoiceForm.speseArt15) || 0;
                  if (!currentSpese && selectedCase) {
                    const dirittiBolli = Number(selectedCase.importoDirittiBolli)
                      || ((Number(selectedCase.importoDiritti) || 0) + (Number(selectedCase.importoBolli) || 0));
                    if (dirittiBolli > 0) {
                      updates.speseArt15 = String(dirittiBolli);
                      setDisplaySpeseArt15(formatEU(dirittiBolli));
                    }
                  }

                  if (Object.keys(updates).length > 0) {
                    setInvoiceForm(prev => ({ ...prev, ...updates }));
                  }
                }
              }}
              placeholder="Aggiungi pratica..."
              searchPlaceholder="Digita numero, oggetto..."
              emptyMessage="Nessuna pratica trovata."
              disabled={!invoiceForm.clientId}
              renderItem={(item) => (
                <div className="flex items-center gap-2 min-w-0">
                  {item.value === "none" ? (
                    <span className="text-slate-500 italic">{item.label}</span>
                  ) : (
                    <>
                      <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{item.caseData?.caseNumber}</div>
                        <div className="text-xs text-slate-500 truncate">{item.caseData?.subject}</div>
                      </div>
                      {item.caseData?.estimatedFee > 0 && (
                        <span className="text-xs font-mono text-green-600 flex-shrink-0">
                          {formatEU(item.caseData.estimatedFee)} €
                        </span>
                      )}
                    </>
                  )}
                </div>
              )}
              data-testid="invoice-case-select"
            />
          </div>
          
          {/* Manual/Free Rows Section */}
          <div className="lg:col-span-6 border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <Label className="font-medium">Righe Manuali (IVA 22%)</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addManualRow}
                className="h-7 text-xs"
              >
                <Plus className="h-3 w-3 mr-1" />
                Aggiungi Riga
              </Button>
            </div>
            
            {manualRows.length > 0 && (
              <div className="space-y-2 mb-3">
                {manualRows.map((row, index) => (
                  <div key={row.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-md">
                    <span className="text-xs text-slate-400 w-6">{index + 1}.</span>
                    <Input
                      placeholder="Descrizione"
                      value={row.description}
                      onChange={(e) => updateManualRow(row.id, 'description', e.target.value)}
                      disabled={row.isAdvanceDeduction}
                      className={cn("flex-1 h-8 text-sm", row.isAdvanceDeduction && "bg-amber-50")}
                    />
                    <Input
                      placeholder="0,00"
                      value={row.displayAmount || row.amount}
                      onChange={(e) => updateManualRow(row.id, 'amount', e.target.value)}
                      onBlur={() => handleManualRowAmountBlur(row.id)}
                      disabled={row.isAdvanceDeduction}
                      className={cn("w-28 h-8 text-sm text-right font-mono", row.isAdvanceDeduction && "bg-amber-50 text-amber-800")}
                      inputMode="decimal"
                    />
                    <span className="text-xs text-slate-400">€</span>
                    {row.isAdvanceDeduction ? (
                      <span className="text-[10px] font-medium text-amber-700 w-14 text-center">ACCONTO</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => removeManualRow(row.id)}
                        className="text-red-400 hover:text-red-600 p-1"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
                <div className="flex justify-end text-sm text-slate-600">
                  <span>Totale righe manuali: <strong className="font-mono text-blue-600">€ {formatEU(manualRowsTotal)}</strong></span>
                </div>
              </div>
            )}
            
            {manualRows.length === 0 && (
              <p className="text-xs text-slate-400 italic">Nessuna riga manuale. Usa "Aggiungi Riga" per inserire voci libere.</p>
            )}
          </div>
          
          {/* Imponibile with Lordo/Netto Toggle */}
          <div className="lg:col-span-6 border-t pt-3">
            {(() => {
              const sel = clients.find(c => String(c.id) === invoiceForm.clientId);
              const isPrivateNoVat = sel && (sel.clientType === "Privato" || !sel.vatNumber || String(sel.vatNumber).trim() === "");
              if (!isPrivateNoVat) return null;
              return (
                <div className="mb-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800 flex items-center gap-2" data-testid="private-no-vat-hint">
                  <Calculator className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    Cliente privato / senza P.IVA — inserisci l'importo <strong>lordo</strong> (IVA inclusa).
                    Il sistema scorpora imponibile e IVA automaticamente.
                  </span>
                </div>
              );
            })()}
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="imponibile" className={cn("font-medium", importoLordo && "text-green-700")}>
                {importoLordo ? "Totale IVA inclusa (EUR) *" : "Importo Netto (EUR) *"}
              </Label>
              <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => handleImportoLordoToggle(false)}
                  className={cn(
                    "px-3 py-1 text-xs rounded-md transition-colors",
                    !importoLordo 
                      ? "bg-white shadow-sm text-slate-800 font-medium" 
                      : "text-slate-500 hover:text-slate-700"
                  )}
                  data-testid="toggle-netto"
                >
                  Netto (+ IVA)
                </button>
                <button
                  type="button"
                  onClick={() => handleImportoLordoToggle(true)}
                  className={cn(
                    "px-3 py-1 text-xs rounded-md transition-colors",
                    importoLordo 
                      ? "bg-green-600 shadow-sm text-white font-medium" 
                      : "text-slate-500 hover:text-slate-700"
                  )}
                  data-testid="toggle-lordo"
                >
                  Lordo (IVA inclusa)
                </button>
              </div>
            </div>
            <Input
              id="imponibile"
              type="text"
              inputMode="decimal"
              data-testid="invoice-imponibile-input"
              value={displayImponibile}
              onChange={handleImponibileChange}
              onBlur={handleImponibileBlur}
              placeholder="0,00"
              className={cn(
                "text-right font-mono",
                importoLordo 
                  ? "text-xl font-bold bg-green-50 border-green-300 focus:border-green-500 focus:ring-green-200" 
                  : "text-lg"
              )}
            />
            <p className="text-xs text-slate-500 mt-1">
              {importoLordo 
                ? <>Inserisci il totale <strong>lordo</strong> — imponibile e IVA vengono scorporati automaticamente: <strong>Imponibile</strong> € {formatEU(imponibileEffettivo)} · <strong>IVA</strong> € {formatEU(ivaAmount)}</>
                : <>Importo <strong>netto</strong> — l'IVA verrà aggiunta al totale</>
              }
            </p>
          </div>
          
          {/* Contributo Integrativo 4% with Toggle */}
          <div className="lg:col-span-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Label htmlFor="contributoIntegrativo" className="font-medium">
                  Contributo Integrativo 4%
                </Label>
                <Calculator className="h-4 w-4 text-slate-400" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">
                  {contributoEnabled ? "Auto" : "Disabilitato"}
                </span>
                <Switch
                  id="contributo-toggle"
                  checked={contributoEnabled}
                  onCheckedChange={handleContributoToggle}
                  data-testid="contributo-toggle"
                />
              </div>
            </div>
            
            <div className={cn(
              "flex gap-3 items-center p-3 rounded-lg border transition-colors",
              contributoEnabled 
                ? "bg-blue-50 border-blue-200" 
                : "bg-slate-50 border-slate-200 opacity-60"
            )}>
              <div className="flex-1">
                <Input
                  id="contributoIntegrativo"
                  type="text"
                  data-testid="invoice-contributo-input"
                  value={contributoEnabled ? formatEU(contributoIntegrativo) : (invoiceForm.contributoIntegrativo || "")}
                  onChange={(e) => {
                    if (!contributoEnabled) {
                      setInvoiceForm({ ...invoiceForm, contributoIntegrativo: e.target.value });
                    }
                  }}
                  disabled={contributoEnabled}
                  placeholder="0,00"
                  className={cn("text-right font-mono", contributoEnabled && "bg-white/70")}
                />
              </div>
              <div className="text-right min-w-[80px]">
                <p className="text-sm font-semibold text-blue-700">
                  € {formatEU(contributoIntegrativo)}
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <span className={cn(
                "inline-block w-2 h-2 rounded-full",
                contributoEnabled ? "bg-green-500" : "bg-slate-300"
              )}></span>
              Contributo Cassa Previdenza {contributoEnabled ? "(calcolato automaticamente)" : "(manuale)"} — soggetto a IVA
            </p>
          </div>
          
          {/* IVA */}
          <div className="lg:col-span-3">
            <Label htmlFor="iva">IVA (%)</Label>
            <Select
              value={String(invoiceForm.iva)}
              onValueChange={(value) =>
                setInvoiceForm({ ...invoiceForm, iva: value })
              }
            >
              <SelectTrigger data-testid="invoice-iva-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="22">22%</SelectItem>
                <SelectItem value="10">10%</SelectItem>
                <SelectItem value="4">4%</SelectItem>
                <SelectItem value="0">Esente (0%)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Spese di Spedizione (11/06/2026) — addebito additivo NON
              soggetto a IVA. Stampato in PDF solo se > 0. Default 0
              per backward compatibility. */}
          <div className="lg:col-span-3">
            <div className="flex items-center gap-2 mb-1">
              <Label htmlFor="shippingExpenses">Spese di Spedizione (EUR)</Label>
            </div>
            <Input
              id="shippingExpenses"
              type="text"
              inputMode="decimal"
              data-testid="invoice-shipping-expenses-input"
              value={invoiceForm.shippingExpensesAmount || ""}
              onChange={(e) => {
                // Accetta formato IT con virgola; converto a number alla blur.
                setInvoiceForm({ ...invoiceForm, shippingExpensesAmount: e.target.value });
              }}
              onBlur={(e) => {
                const raw = String(e.target.value || "").replace(",", ".");
                const n = parseFloat(raw);
                setInvoiceForm({
                  ...invoiceForm,
                  shippingExpensesAmount: Number.isFinite(n) && n > 0 ? n : 0,
                });
              }}
              placeholder="0,00"
              className="text-right font-mono"
            />
            <p className="text-xs text-slate-500 mt-1">
              Addebito aggiuntivo NON soggetto a IVA, sommato al totale.
              Stampato in PDF solo se &gt; 0.
            </p>
          </div>

          {/* Spese Art. 15 - sempre escluse da IVA con marca da bollo automatica */}
          <div className="lg:col-span-6">
            <div className="flex items-center gap-2 mb-1">
              <Label htmlFor="speseArt15">Spese Art. 15 (EUR)</Label>
              <AlertTriangle className="h-3 w-3 text-amber-500" />
            </div>
            <Input
              id="speseArt15"
              type="text"
              inputMode="decimal"
              data-testid="invoice-spese-art15-input"
              value={displaySpeseArt15}
              onChange={handleSpeseArt15Change}
              onBlur={handleSpeseArt15Blur}
              placeholder="0,00"
              className="text-right font-mono"
            />
            <p className="text-xs text-amber-600 mt-1 font-medium">
              Spese anticipate in nome e per conto — SEMPRE escluse da IVA
            </p>
            {/* Marca da bollo automatica alert */}
            <div className={cn(
              "mt-2 p-2 rounded-lg border flex items-center gap-2 text-xs",
              bolloAutomatico > 0 
                ? "bg-green-50 border-green-200 text-green-800" 
                : "bg-slate-50 border-slate-200 text-slate-500"
            )}>
              <Stamp className="h-4 w-4" />
              {bolloAutomatico > 0 ? (
                <span className="font-medium">
                  Marca da bollo € 2,00 aggiunta automaticamente (Art. 15 &gt; € 77,47)
                </span>
              ) : (
                <span>Marca da bollo: non applicabile (Art. 15 ≤ € 77,47)</span>
              )}
            </div>
            {/* Art.15 over-flow guard — only shown in LORDO mode when the
                bolli would consume more than the operator-entered gross. */}
            {art15ExceedsLordo && (
              <div
                className="mt-2 p-3 rounded-lg border bg-red-50 border-red-200 text-red-800 text-xs flex items-start gap-2"
                data-testid="art15-exceeds-lordo-warning"
              >
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold mb-1">
                    Spese Art. 15 (€ {speseArt15.toFixed(2).replace(".", ",")})
                    superano il totale Lordo inserito (€ {grossPoolForArt15.toFixed(2).replace(".", ",")}).
                  </p>
                  <p>
                    In modalità Lordo i bolli vengono assorbiti dal totale
                    fattura. Aumenta l'importo lordo o riduci le Spese
                    Art. 15 per evitare un'imponibile a zero e un PDF
                    incoerente.
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {/* Payment Method */}
          <div className="lg:col-span-6">
            <Label htmlFor="paymentMethod">Metodo Pagamento</Label>
            <Select
              value={invoiceForm.paymentMethod}
              onValueChange={(value) =>
                setInvoiceForm({ ...invoiceForm, paymentMethod: value })
              }
            >
              <SelectTrigger data-testid="invoice-payment-method-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Bonifico">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Bonifico bancario vista fattura
                  </div>
                </SelectItem>
                <SelectItem value="POS">
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4" />
                    Pagamento effettuato con POS con quietanza in fattura
                  </div>
                </SelectItem>
                <SelectItem value="Contanti">
                  <div className="flex items-center gap-2">
                    <Banknote className="h-4 w-4" />
                    Pagamento effettuato in contanti con quietanza
                  </div>
                </SelectItem>
                <SelectItem value="ContantiVistaFattura">
                  <div className="flex items-center gap-2">
                    <Banknote className="h-4 w-4" />
                    Contanti vista fattura
                  </div>
                </SelectItem>
                <SelectItem value="Assegno">
                  <div className="flex items-center gap-2">
                    <Euro className="h-4 w-4" />
                    Assegno Bancario
                  </div>
                </SelectItem>
                <SelectItem value="RimessaDiretta">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Rimessa diretta / presentazione fattura
                  </div>
                </SelectItem>
                <SelectItem value="Altro">
                  <div className="flex items-center gap-2">
                    <MoreHorizontal className="h-4 w-4" />
                    Altro
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* IBAN Info Box - shown when Bonifico is selected and IBAN is configured */}
          <div className={cn(
            "lg:col-span-12 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2",
            showIbanInfo ? "" : "hidden"
          )}>
            <div className="flex items-center gap-2 flex-wrap text-xs text-blue-800">
              <Building2 className="h-4 w-4 text-blue-600" />
              <span className="font-medium">Coordinate bancarie:</span>
              <span className={cn("font-mono font-semibold text-blue-900", settings?.iban ? "" : "hidden")}>
                {settings?.iban}
              </span>
              <span className={cn(settings?.bankName ? "" : "hidden")}>
                · {settings?.bankName}
              </span>
              <span className="text-blue-600 italic">riportate automaticamente in fattura</span>
            </div>
          </div>
          
          {/* Mark as Paid checkbox - only for immediate payment methods */}
          <div className={cn(
            "lg:col-span-12 bg-green-50 border border-green-200 rounded-lg p-3",
            canMarkAsPaid && invoiceForm.status !== "Pagata" ? "" : "hidden"
          )}>
            <div className="flex items-center space-x-3">
              <Checkbox
                id="markAsPaid"
                checked={invoiceForm.markAsPaid || false}
                onCheckedChange={(checked) =>
                  setInvoiceForm({
                    ...invoiceForm,
                    markAsPaid: checked,
                    // 27/02/2026 — All'attivazione precompiliamo la data
                    // effettiva con quella della fattura. L'operatore può
                    // poi modificarla per indicare una data antecedente
                    // (es. acconto incassato giorni prima della fattura).
                    paymentDate: checked
                      ? (invoiceForm.paymentDate || invoiceForm.invoiceDate || new Date().toISOString().split("T")[0])
                      : invoiceForm.paymentDate,
                  })
                }
                data-testid="invoice-mark-paid-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="markAsPaid" className="text-green-800 font-medium cursor-pointer">
                  <CheckCircle2 className="h-4 w-4 inline mr-2" />
                  Segna come pagata immediatamente (con quietanza)
                </Label>
                <p className="text-xs text-green-700 mt-1">
                  Il pagamento {invoiceForm.paymentMethod === "POS" ? "con POS/Carta" : "in contanti"} è già stato ricevuto — La quietanza sarà inclusa in fattura
                </p>
              </div>
            </div>
            {/* 27/02/2026 — Quando il pagamento è già avvenuto consentiamo
                all'operatore di indicare la data effettiva, che può essere
                antecedente alla data della fattura (es. acconto ricevuto
                prima dell'emissione). La data NON può essere posteriore
                alla data della fattura. */}
            {invoiceForm.markAsPaid && (
              <div className="mt-3 pt-3 border-t border-green-200">
                <Label htmlFor="actualPaymentDate" className="text-green-800 text-sm font-medium">
                  Data effettiva del pagamento
                </Label>
                <Input
                  id="actualPaymentDate"
                  type="date"
                  className="mt-1 max-w-[200px] bg-white"
                  value={invoiceForm.paymentDate || invoiceForm.invoiceDate || ""}
                  max={invoiceForm.invoiceDate || undefined}
                  onChange={(e) =>
                    setInvoiceForm({ ...invoiceForm, paymentDate: e.target.value })
                  }
                  data-testid="invoice-actual-payment-date-input"
                />
                <p className="text-[11px] text-green-700 mt-1">
                  Default: data della fattura ({invoiceForm.invoiceDate || "—"}). Modifica se il pagamento è avvenuto in data antecedente.
                </p>
              </div>
            )}
          </div>
          
          {/* Payment Date - for already paid invoices */}
          <div className={cn("lg:col-span-4", invoiceForm.status === "Pagata" ? "" : "hidden")}>
            <Label htmlFor="paymentDate">Data Pagamento</Label>
            <Input
              id="paymentDate"
              type="date"
              data-testid="invoice-payment-date-input"
              value={invoiceForm.paymentDate || ""}
              onChange={(e) =>
                setInvoiceForm({ ...invoiceForm, paymentDate: e.target.value })
              }
            />
          </div>
          
          <div className="lg:col-span-5">
            <Label htmlFor="notes">Descrizione</Label>
            <Textarea
              id="notes"
              data-testid="invoice-notes-textarea"
              value={invoiceForm.notes}
              onChange={(e) =>
                setInvoiceForm({ ...invoiceForm, notes: e.target.value })
              }
              rows={2}
              placeholder="Descrizione della prestazione (auto-compilata dalla pratica collegata)"
            />
            <p className="text-xs text-slate-500 mt-1">
              Viene compilata automaticamente dalla pratica collegata, ma può essere modificata
            </p>
          </div>
          
          {/* Totals Summary */}
          <div className="lg:col-span-7 rounded-lg overflow-hidden border border-slate-200">
            {/* TOTALE FATTURA - Primary value, visually dominant */}
            <div className="bg-green-600 px-4 py-3 flex justify-between items-center" data-testid="invoice-total-banner">
              <span className="text-white font-bold text-lg">Totale Fattura (IVA inclusa)</span>
              <span className="text-white font-bold text-2xl font-mono tracking-tight">€ {formatEU(totale)}</span>
            </div>
            
            {/* Detail breakdown */}
            <div className="bg-slate-50 p-3 grid grid-cols-1 xl:grid-cols-2 gap-x-5 gap-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">
                  {importoLordo ? "Imponibile principale (scorporato):" : "Imponibile principale:"}
                </span>
                <span className="font-medium font-mono">€ {formatEU(imponibileEffettivo)}</span>
              </div>
              {/* Manual rows breakdown — labels adapt to LORDO/NET mode.
                  Always mounted with `hidden` via CSS to keep React 19
                  reconciliation stable across mode flips. */}
              <div
                className={cn(
                  "flex justify-between text-sm text-emerald-700",
                  manualRowsTotalInput > 0 ? "" : "hidden"
                )}
                data-testid="invoice-breakdown-manual-rows"
              >
                <span className="flex items-center gap-1">
                  <Plus className="h-3 w-3" />
                  {importoLordo
                    ? `Righe manuali lorde (${manualRows.length}):`
                    : `Righe manuali (${manualRows.length}):`}
                </span>
                <span className="font-medium font-mono">
                  € {formatEU(manualRowsTotalInput)}
                </span>
              </div>
              {/* In LORDO mode also surface the scorporated net contribution
                  of the manual rows so the operator sees exactly what feeds
                  the IVA base. Hidden in NET mode (where input == net). */}
              <div
                className={cn(
                  "flex justify-between text-xs text-emerald-700/80 pl-5",
                  importoLordo && manualRowsTotalInput > 0 ? "" : "hidden"
                )}
                data-testid="invoice-breakdown-manual-rows-scorporato"
              >
                <span>↳ di cui imponibile scorporato:</span>
                <span className="font-mono">€ {formatEU(manualRowsTotal)}</span>
              </div>
              <div
                className={cn(
                  "flex justify-between text-sm bg-white/50 p-1 rounded",
                  manualRowsTotalInput > 0 ? "" : "hidden"
                )}
                data-testid="invoice-breakdown-imponibile-totale"
              >
                <span className="text-slate-600 font-medium">
                  {importoLordo ? "Imponibile totale (scorporato):" : "Imponibile totale:"}
                </span>
                <span className="font-semibold font-mono">€ {formatEU(imponibileWithManualRows)}</span>
              </div>
              <div className={cn(
                "flex justify-between text-sm text-blue-700",
                contributoIntegrativo > 0 ? "" : "hidden"
              )}>
                <span className="flex items-center gap-1">
                  <Calculator className="h-3 w-3" />
                  {importoLordo
                    ? "Contributo Integrativo 4% (scorporato):"
                    : "Contributo Integrativo 4%:"}
                </span>
                <span className="font-medium font-mono">€ {formatEU(contributoIntegrativo)}</span>
              </div>
              <Separator className={cn(contributoIntegrativo > 0 ? "" : "hidden")} />
              <div className={cn(
                "flex justify-between text-sm bg-white/50 p-1 rounded",
                contributoIntegrativo > 0 ? "" : "hidden"
              )}>
                <span className="text-slate-600 font-medium">Base imponibile IVA:</span>
                <span className="font-semibold font-mono">€ {formatEU(baseIVA)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">
                  {importoLordo ? `IVA (${ivaPercentage}%) scorporata:` : `IVA (${ivaPercentage}%):`}
                </span>
                <span className="font-medium font-mono">€ {formatEU(ivaAmount)}</span>
              </div>
              
              {/* Art. 15 - clearly separated and marked as exempt */}
              <div className={cn(
                "flex justify-between text-sm bg-amber-50 border border-amber-200 rounded p-2 mt-2",
                speseArt15Input > 0 ? "" : "hidden"
              )}>
                <span className="text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" />
                  Spese Art. 15 (escluse da IVA):
                </span>
                <span className="font-semibold text-amber-800 font-mono">€ {formatEU(speseArt15Input)}</span>
              </div>

              {/* Spese di Spedizione (11/06/2026) — addebito additivo NON
                  soggetto a IVA, sommato al totale. Reso visibile nel
                  breakdown per dare conferma operativa che la voce è
                  stata conteggiata (in precedenza il totale la includeva
                  ma il breakdown la nascondeva → l'operatore non vedeva
                  la riga e pensava fosse stata omessa). */}
              <div
                className={cn(
                  "flex justify-between text-sm bg-sky-50 border border-sky-200 rounded p-2",
                  shippingExpensesInput > 0 ? "" : "hidden"
                )}
                data-testid="invoice-breakdown-shipping-expenses"
              >
                <span className="text-sky-700 flex items-center gap-1">
                  <Plus className="h-3 w-3" />
                  Spese di Spedizione (escluse da IVA):
                </span>
                <span className="font-semibold text-sky-800 font-mono">€ {formatEU(shippingExpensesInput)}</span>
              </div>
              
              {/* Marca da bollo row */}
              <div className={cn(
                "flex justify-between text-sm bg-green-50 border border-green-200 rounded p-2",
                bolloAutomatico > 0 ? "" : "hidden"
              )}>
                <span className="text-green-700 flex items-center gap-1">
                  <Stamp className="h-3 w-3" />
                  Imposta di bollo (Art. 15 &gt; €77,47):
                </span>
                <span className="font-semibold text-green-800 font-mono">€ {formatEU(bolloAutomatico)}</span>
              </div>
              
              {/* Arrotondamento row */}
              <div className={cn(
                "flex justify-between text-sm bg-purple-50 border border-purple-200 rounded p-2",
                arrotondamento !== 0 ? "" : "hidden"
              )}>
                <span className="text-purple-700 flex items-center gap-1">
                  <Calculator className="h-3 w-3" />
                  Arrotondamento:
                </span>
                <span className={cn(
                  "font-semibold font-mono",
                  arrotondamento > 0 ? "text-purple-800" : "text-purple-800"
                )}>
                  {arrotondamento > 0 ? "+" : ""}€ {formatEU(arrotondamento)}
                </span>
              </div>

              {/* Split Payment toggle + breakdown (07/06/2026).
                  Bug fix 11/06/2026 — Visibile SOLO se l'anagrafica
                  cliente selezionata ha `splitPaymentEnabled === true`.
                  Se il cliente non è abilitato, la sezione viene
                  nascosta completamente e qualsiasi flag residuo sulla
                  fattura viene forzato a false in un effect dedicato. */}
              {(() => {
                const selectedClientObj = clients.find(
                  (c) => String(c.id) === String(invoiceForm.clientId)
                );
                const clientAllowsSplit = !!(selectedClientObj && selectedClientObj.splitPaymentEnabled === true);
                if (!clientAllowsSplit) return null;
                return (
                  <div
                    className="bg-cyan-50 border border-cyan-200 rounded p-2 mt-2"
                    data-testid="invoice-split-payment-section"
                  >
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="invoice-split-payment-checkbox"
                    checked={!!invoiceForm.splitPaymentEnabled}
                    onChange={(e) =>
                      setInvoiceForm({
                        ...invoiceForm,
                        splitPaymentEnabled: e.target.checked,
                      })
                    }
                    className="h-4 w-4 rounded border-cyan-400 text-cyan-600 focus:ring-cyan-500"
                  />
                  <span className="text-sm text-cyan-900 font-medium flex-1">
                    Split Payment (art. 17-ter DPR 633/1972)
                  </span>
                </label>
                {invoiceForm.splitPaymentEnabled && (
                  <div className="mt-2 space-y-1 pl-6">
                    <div className="flex justify-between text-sm text-red-700">
                      <span className="flex items-center gap-1">
                        − IVA a carico del committente:
                      </span>
                      <span className="font-medium font-mono">
                        −€ {formatEU(ivaAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between text-base bg-cyan-100 border border-cyan-300 rounded p-2 mt-1">
                      <span className="text-cyan-900 font-bold">
                        Netto a pagare:
                      </span>
                      <span
                        className="text-cyan-900 font-bold font-mono tracking-tight"
                        data-testid="invoice-net-amount-due"
                      >
                        € {formatEU(Math.max(0, (Number(totale) || 0) - (Number(ivaAmount) || 0)))}
                      </span>
                    </div>
                  </div>
                )}
              </div>
                );
              })()}
            </div>
          </div>
          
          {/* Error Alert */}
          {saveError && (
            <div 
              className="lg:col-span-12 p-4 bg-red-100 border border-red-400 rounded-lg text-red-800 flex items-start gap-3"
              data-testid="invoice-save-error"
            >
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold">Errore durante il salvataggio</p>
                <p className="text-sm mt-1">{saveError}</p>
              </div>
            </div>
          )}
        </div>
        </div>
        <DialogFooter className="flex gap-2">
          <Button variant="outline" onClick={guardedReset}>
            Annulla
          </Button>
          <Button
            variant="secondary"
            data-testid="preview-invoice-button"
            onClick={handlePreview}
            /**
             * Enable conditions:
             *   - a client must be selected
             *   - the invoice must have at least ONE billable amount: either
             *     the main "imponibile" field is populated, OR there is at
             *     least one manual row with a non-zero amount.
             *   - the preview isn't already in flight
             *
             * (Previously the guard required `invoiceForm.imponibile` to be
             * truthy, which made the button unreachable on invoices composed
             * solely of free-text manual rows — the exact case reported by
             * the user.)
             */
            disabled={
              !invoiceForm.clientId ||
              (
                !(parseFloat(invoiceForm.imponibile) > 0) &&
                manualRowsTotalInput <= 0
              ) ||
              isPreviewLoading
            }
          >
            {isPreviewLoading ? (
              <>Generazione...</>
            ) : (
              <>
                <Eye className="mr-2 h-4 w-4" />
                Anteprima PDF
              </>
            )}
          </Button>
          <Button 
            data-testid="save-invoice-button" 
            onClick={handleSave} 
            disabled={isSaving}
            className="gis-btn-primary"
          >
            {isSaving
              ? "Salvataggio..."
              : (isEditing
                  ? "Salva Modifiche"
                  : (invoiceForm.documentType === "proforma"
                      ? "Crea Proforma"
                      : "Crea Fattura"))}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
